import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/utils';
import { Plus, Bell, AlertTriangle, Clock, User, Home, Check, MessageSquare, Edit, Trash2, Eye, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const CommunicationsPage = () => {
  const { user, api } = useAuth();
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedComm, setSelectedComm] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [houses, setHouses] = useState([]);
  const [filter, setFilter] = useState('all');
  
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'house_manager';
  const isWorker = user?.role === 'worker';
  const canCreate = isAdmin || isManager || isWorker;
  const canCreateImportant = isAdmin || isManager; // Workers can only create general posts

  const [newComm, setNewComm] = useState({
    title: '',
    message: '',
    comm_type: 'general',
    participant_ids: [],
    house_ids: [],
    visible_roles: ['admin', 'house_manager', 'worker'],
    expiry_date: '',
    requires_acknowledgment: false
  });

  useEffect(() => {
    fetchCommunications();
    fetchParticipants();
    fetchHouses();
    fetchUsers();
  }, []);

  const fetchCommunications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/communications');
      setCommunications(response.data);
    } catch (error) {
      toast.error('Failed to load communications');
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async () => {
    try {
      const response = await api.get('/participants');
      setParticipants(response.data);
    } catch (error) {
      console.error('Failed to load participants');
    }
  };

  const fetchHouses = async () => {
    try {
      const response = await api.get('/houses');
      setHouses(response.data);
    } catch (error) {
      console.error('Failed to load houses');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleCreateComm = async () => {
    if (!newComm.title || !newComm.message) {
      toast.error('Title and message are required');
      return;
    }

    try {
      await api.post('/communications', {
        ...newComm,
        expiry_date: newComm.expiry_date || null
      });
      toast.success('Communication created successfully');
      setCreateDialogOpen(false);
      setNewComm({
        title: '',
        message: '',
        comm_type: 'general',
        participant_ids: [],
        house_ids: [],
        visible_roles: ['admin', 'house_manager', 'worker'],
        expiry_date: '',
        requires_acknowledgment: false
      });
      fetchCommunications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create communication');
    }
  };

  const handleAcknowledge = async (commId) => {
    try {
      await api.post(`/communications/${commId}/acknowledge`);
      toast.success('Acknowledged');
      fetchCommunications();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const handleArchive = async (commId) => {
    if (!window.confirm('Are you sure you want to archive this communication?')) return;
    
    try {
      await api.delete(`/communications/${commId}`);
      toast.success('Communication archived');
      fetchCommunications();
    } catch (error) {
      toast.error('Failed to archive communication');
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'important':
        return <Badge className="bg-red-500/20 text-red-600 border-red-500/30"><AlertTriangle className="h-3 w-3 mr-1" /> Important</Badge>;
      case 'temporary':
        return <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30"><Clock className="h-3 w-3 mr-1" /> Temporary</Badge>;
      default:
        return <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30"><MessageSquare className="h-3 w-3 mr-1" /> General</Badge>;
    }
  };

  const filteredComms = communications.filter(comm => {
    if (filter === 'all') return true;
    if (filter === 'important') return comm.comm_type === 'important';
    if (filter === 'unread') return !comm.acknowledged_by?.includes(user?.id);
    return true;
  });

  const toggleParticipant = (participantId) => {
    const current = newComm.participant_ids;
    if (current.includes(participantId)) {
      setNewComm({...newComm, participant_ids: current.filter(id => id !== participantId)});
    } else {
      setNewComm({...newComm, participant_ids: [...current, participantId]});
    }
  };

  const toggleHouse = (houseId) => {
    const current = newComm.house_ids;
    if (current.includes(houseId)) {
      setNewComm({...newComm, house_ids: current.filter(id => id !== houseId)});
    } else {
      setNewComm({...newComm, house_ids: [...current, houseId]});
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader 
        title="Internal Communications"
        description="Important notices and updates for staff"
        actions={
          canCreate && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Communication
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Communication</DialogTitle>
                  <DialogDescription>
                    Create an internal notice for staff
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input 
                      value={newComm.title}
                      onChange={(e) => setNewComm({...newComm, title: e.target.value})}
                      placeholder="Communication title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Message *</Label>
                    <Textarea 
                      value={newComm.message}
                      onChange={(e) => setNewComm({...newComm, message: e.target.value})}
                      placeholder="Enter your message..."
                      rows={5}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={newComm.comm_type} onValueChange={(v) => setNewComm({...newComm, comm_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          {canCreateImportant && (
                            <SelectItem value="important">Important</SelectItem>
                          )}
                          <SelectItem value="temporary">Temporary</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Expiry Date (optional)</Label>
                      <Input 
                        type="datetime-local"
                        value={newComm.expiry_date}
                        onChange={(e) => setNewComm({...newComm, expiry_date: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Link to Participants (optional)</Label>
                    <div className="flex flex-wrap gap-2 p-2 border rounded-lg max-h-[100px] overflow-y-auto">
                      {participants.map(p => (
                        <Badge 
                          key={p.id}
                          variant={newComm.participant_ids.includes(p.id) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleParticipant(p.id)}
                        >
                          <User className="h-3 w-3 mr-1" />
                          {p.first_name} {p.last_name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Link to Houses (optional)</Label>
                    <div className="flex flex-wrap gap-2 p-2 border rounded-lg">
                      {houses.map(h => (
                        <Badge 
                          key={h.id}
                          variant={newComm.house_ids.includes(h.id) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleHouse(h.id)}
                        >
                          <Home className="h-3 w-3 mr-1" />
                          {h.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {canCreateImportant && (
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="require-ack"
                        checked={newComm.requires_acknowledgment}
                        onCheckedChange={(checked) => setNewComm({...newComm, requires_acknowledgment: checked})}
                      />
                      <Label htmlFor="require-ack" className="cursor-pointer">
                        Require staff to acknowledge this communication
                      </Label>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateComm}>
                    Create Communication
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {/* Filters */}
      <div className="flex gap-2">
        <Button 
          variant={filter === 'all' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('all')}
        >
          All
        </Button>
        <Button 
          variant={filter === 'important' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('important')}
        >
          <AlertTriangle className="h-4 w-4 mr-1" />
          Important
        </Button>
        <Button 
          variant={filter === 'unread' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('unread')}
        >
          <Bell className="h-4 w-4 mr-1" />
          Unread
        </Button>
      </div>

      {/* Communications List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : filteredComms.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Communications</h3>
          <p className="text-muted-foreground">There are no communications to display.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredComms.map(comm => {
            const isAcknowledged = comm.acknowledged_by?.includes(user?.id);
            
            return (
              <Card 
                key={comm.id}
                className={cn(
                  "transition-all hover:shadow-md",
                  comm.comm_type === 'important' && "border-red-500/30 bg-red-500/5",
                  comm.is_expired && "opacity-60"
                )}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {getTypeBadge(comm.comm_type)}
                        {comm.is_expired && (
                          <Badge variant="outline" className="text-muted-foreground">Expired</Badge>
                        )}
                        {comm.requires_acknowledgment && isAcknowledged && (
                          <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                            <Check className="h-3 w-3 mr-1" /> Acknowledged
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg">{comm.title}</CardTitle>
                    </div>
                    <div className="flex gap-2">
                      {comm.requires_acknowledgment && !isAcknowledged && (
                        <Button size="sm" onClick={() => handleAcknowledge(comm.id)}>
                          <Check className="h-4 w-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                      {(isAdmin || isManager) && (
                        <Button variant="ghost" size="icon" onClick={() => handleArchive(comm.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap mb-4">
                    {comm.message}
                  </p>
                  
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    {comm.participant_names?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {comm.participant_names.join(', ')}
                      </div>
                    )}
                    {comm.house_names?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Home className="h-3 w-3" />
                        {comm.house_names.join(', ')}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(new Date(comm.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                    {comm.created_by_name && (
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {comm.created_by_name}
                      </div>
                    )}
                    {comm.expiry_date && (
                      <div className="flex items-center gap-1 text-amber-600">
                        <Clock className="h-3 w-3" />
                        Expires: {format(new Date(comm.expiry_date), 'MMM d, yyyy')}
                      </div>
                    )}
                  </div>
                  
                  {/* Acknowledgment Status - Admin/Manager Only */}
                  {comm.requires_acknowledgment && (isAdmin || isManager) && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        Acknowledged by ({comm.acknowledged_by?.length || 0}):
                      </p>
                      {comm.acknowledged_by?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {comm.acknowledged_by.map(userId => {
                            const acknowledgedUser = users.find(u => u.id === userId);
                            return (
                              <Badge key={userId} variant="secondary" className="text-xs">
                                {acknowledgedUser ? `${acknowledgedUser.first_name} ${acknowledgedUser.last_name}` : userId}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No acknowledgments yet</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedComm?.title}</DialogTitle>
          </DialogHeader>
          {selectedComm && (
            <div className="space-y-4">
              <p className="text-sm whitespace-pre-wrap">{selectedComm.message}</p>
              <div className="text-xs text-muted-foreground">
                <p>Created by: {selectedComm.created_by_name}</p>
                <p>Date: {format(new Date(selectedComm.created_at), 'PPpp')}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunicationsPage;
